import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingSpinner } from './LoadingSpinner';
import { 
  getSleeperUser, 
  getSleeperLeagues, 
  importRosterAndSettingsFromSleeper,
  type SleeperUser,
  type SleeperLeague,
  type SleeperImportResult
} from '@/utils/sleeperRosterIntegration';
import type { Player } from '@/types/player';
import type { LeagueSettings } from '@/types/trade';

interface SleeperImportProps {
  onImportSuccess: (players: Player[], leagueSettings?: LeagueSettings, leagueName?: string) => void;
  availablePlayers: Player[];
}

export const SleeperImport: React.FC<SleeperImportProps> = ({ onImportSuccess, availablePlayers }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'username' | 'leagues' | 'importing'>('username');
  const [username, setUsername] = useState('');
  const [sleeperUser, setSleeperUser] = useState<SleeperUser | null>(null);
  const [leagues, setLeagues] = useState<SleeperLeague[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFindUser = async () => {
    if (!username.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const user = await getSleeperUser(username.trim());
      if (user) {
        setSleeperUser(user);
        
        // Get current season leagues
        const currentSeason = new Date().getFullYear().toString();
        const userLeagues = await getSleeperLeagues(user.user_id, currentSeason);
        
        if (userLeagues.length === 0) {
          setError(`No leagues found for ${user.display_name} in ${currentSeason}`);
        } else {
          setLeagues(userLeagues);
          setStep('leagues');
        }
      } else {
        setError('Sleeper user not found. Please check the username.');
      }
    } catch (err) {
      setError('Failed to find user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportRoster = async (leagueId: string) => {
    if (!sleeperUser) return;
    
    setStep('importing');
    setIsLoading(true);
    setError(null);
    
    try {
      const result: SleeperImportResult = await importRosterAndSettingsFromSleeper(
        sleeperUser.user_id,
        leagueId,
        availablePlayers
      );
      
      if (result.success) {
        onImportSuccess(result.players, result.leagueSettings, result.leagueName);
        setIsOpen(false);
        resetState();
      } else {
        setError(result.error || 'Failed to import roster');
        setStep('leagues');
      }
    } catch (err) {
      setError('Failed to import roster. Please try again.');
      setStep('leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setStep('username');
    setUsername('');
    setSleeperUser(null);
    setLeagues([]);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        Import from Sleeper
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-600 rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-fiesta-yellow">Import from Sleeper</h3>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {step === 'username' && (
                  <div className="space-y-4">
                    <p className="text-gray-300 text-sm">
                      Enter your Sleeper username to import your roster and league scoring settings from your fantasy leagues.
                    </p>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Sleeper Username
                      </label>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleFindUser()}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:ring-fiesta-blue focus:border-fiesta-blue"
                        placeholder="YourSleeperUsername"
                        autoFocus
                      />
                    </div>
                    
                    {error && (
                      <div className="text-red-400 text-sm">{error}</div>
                    )}
                    
                    <button
                      onClick={handleFindUser}
                      disabled={!username.trim() || isLoading}
                      className="w-full px-4 py-2 bg-fiesta-blue hover:bg-fiesta-blue/80 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Finding User...
                        </>
                      ) : (
                        'Find Leagues'
                      )}
                    </button>
                  </div>
                )}

                {step === 'leagues' && sleeperUser && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-gray-300 mb-1">Found user:</div>
                      <div className="font-medium text-fiesta-yellow">{sleeperUser.display_name}</div>
                    </div>
                    
                    <div>
                      <div className="text-sm font-medium text-gray-300 mb-3">
                        Select a league to import roster and scoring settings from:
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {leagues.map((league) => (
                          <div
                            key={league.league_id}
                            className="p-3 bg-gray-700/50 border border-gray-600 rounded-lg hover:bg-gray-700/70 transition-colors cursor-pointer"
                            onClick={() => handleImportRoster(league.league_id)}
                          >
                            <div className="font-medium text-gray-100">{league.name}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {league.total_rosters} teams • {league.status} • {league.season}
                            </div>
                            <div className="text-xs text-fiesta-blue mt-1">
                              ✨ Includes league scoring settings
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {error && (
                      <div className="text-red-400 text-sm">{error}</div>
                    )}
                    
                    <button
                      onClick={() => setStep('username')}
                      className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-gray-100 font-medium rounded-lg transition-colors"
                    >
                      Back
                    </button>
                  </div>
                )}

                {step === 'importing' && (
                  <div className="space-y-4 text-center">
                    <LoadingSpinner size="lg" />
                    <div className="text-gray-300">
                      Importing your roster and league settings from Sleeper...
                    </div>
                    <div className="text-sm text-gray-400">
                      This may take a moment while we match players and convert scoring settings.
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}; 